import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { Scale, ShieldCheck, FileSearch, Lock, Award, Users } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "@/components/motion";

function ParallaxImage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
      <motion.img
        src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=85&w=1600&auto=format&fit=crop"
        alt="Clear Path Repair strategy session with a client"
        style={{ y }}
        className="w-full h-[540px] object-cover scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
      <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Every client gets a real strategy session</p>
    </div>
  );
}

function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

const METHODS = [
  { icon: Scale, title: "FCRA § 611", body: "The Fair Credit Reporting Act forces bureaus to investigate any item you dispute. We file disputes engineered to trigger maximum legal obligation." },
  { icon: ShieldCheck, title: "FDCPA", body: "The Fair Debt Collection Practices Act lets us demand validation from collectors. No validation, no collection, and no tradeline." },
  { icon: FileSearch, title: "Metro 2 Auditing", body: "Every furnisher must report in the Metro 2 format. We audit field-by-field; non-compliant reporting must be deleted under bureau policy and law." },
  { icon: Lock, title: "FACTA", body: "The Fair and Accurate Credit Transactions Act adds identity-theft and accuracy provisions we invoke for fraud-adjacent items." },
];

const TRUST = [
  { icon: Award, label: "CROA Compliant" },
  { icon: Lock, label: "Encrypted Vault" },
  { icon: ShieldCheck, label: "Bonded Firm" },
  { icon: Users, label: "4,200+ Clients" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="py-28 md:py-36 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(93,123,255,0.08),transparent_55%)]" />
        <motion.div
          className="absolute w-[420px] h-[420px] rounded-full bg-[#2E5BFF]/15 blur-[120px] -top-20 -right-20 pointer-events-none"
          animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute w-[320px] h-[320px] rounded-full bg-[#8B45F0]/15 blur-[100px] bottom-0 left-1/4 pointer-events-none"
          animate={{ y: [0, -35, 0], x: [0, 25, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <div className="max-w-7xl mx-auto relative">
          <Eyebrow className="mb-6">Our Story</Eyebrow>
          <MaskedLines
            lines={["Repair that", "stays with you."]}
            className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl"
          >
            We're a credit repair firm that treats your file like our own. We audit, we dispute,
            we educate, and we stick around long after the deletions land.
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <ParallaxImage />
          </Reveal>
          <Reveal delay={0.15}>
            <Eyebrow>Why We Exist</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4 mb-8">
              Most companies fix your credit and <span className="italic text-gold">disappear.</span> <span className="italic text-emerald">We stay.</span>
            </h2>
            <div className="space-y-5 text-slate-400 leading-relaxed text-sm md:text-base">
              <p>
                Here's what usually happens in this industry: a company disputes a few items, collects two or
                three monthly payments, and vanishes. Your score bumps up for a moment, then slides right back,
                because nobody taught you what to do next.
              </p>
              <p>
                Clear Path Repair is different. We stay with you. Long after the disputes are done, you get real
                advice, practical tips, and proven strategies to keep your credit climbing month after month.
              </p>
              <p>
                Because this isn't just repair. It's education and ongoing support, so you never end up in the
                same place again.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Founder & Stats */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-20" data-testid="about-stats">
            {[
              { to: 4200, suffix: "+", label: "Files Restored" },
              { to: 138, suffix: "", prefix: "+", label: "Avg. Points Gained" },
              { to: 93, suffix: "%", label: "First-Cycle Deletions" },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1}>
                <div className="bg-[#101218] border border-white/10 rounded-2xl p-6 md:p-10 text-center hover:border-gold/40 transition-colors duration-300">
                  <p className="font-serif text-3xl md:text-5xl text-white tracking-tight">
                    {s.prefix || ""}<Counter to={s.to} suffix={s.suffix} />
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-3">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div data-testid="founder-card" className="max-w-6xl mx-auto bg-[#101218] border border-white/10 rounded-3xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.37)] relative">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(93,123,255,0.12),transparent_55%)]" />
              <div className="relative grid grid-cols-1 md:grid-cols-5 items-center gap-10 md:gap-14 p-10 md:p-16">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
                  whileInView={{ scale: 1, opacity: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 90, damping: 14, delay: 0.15 }}
                  className="md:col-span-2 flex justify-center"
                >
                  <div className="w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden border-2 border-gold/50 shadow-[0_0_70px_rgba(93,123,255,0.4)]">
                    <img src="/founder.png" alt="Ozza Bejjaji, Founder and CEO of Clear Path Repair" className="w-full h-full object-cover" data-testid="founder-photo" />
                  </div>
                </motion.div>
                <div className="md:col-span-3 text-center md:text-left">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-6">Meet the Founder</p>
                  <p className="font-serif text-2xl md:text-4xl text-white italic leading-snug mb-8">
                    "Most companies fix your credit for two or three months and disappear. That was never good
                    enough for me. We stay with you, teaching the strategies that keep your credit strong for life."
                  </p>
                  <p className="font-serif text-2xl text-white">Ozza Bejjaji</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mt-2">Founder & CEO, Clear Path Repair</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <Eyebrow>Methodology</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
              The legal stack <span className="italic text-gold">behind every deletion.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {METHODS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div data-testid={`method-card-${i}`} className="group bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                    <m.icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-3 tracking-tight">{m.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Standards */}
      <section className="py-24 md:py-32 px-6 lg:px-12 border-t border-white/5 bg-surface/30">
        <div className="max-w-7xl mx-auto text-center">
          <Reveal className="mb-12">
            <Eyebrow>Standards</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white mt-4">
              Built on compliance. <span className="italic text-gold">Driven by results.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap justify-center gap-4" data-testid="trust-badges">
              {TRUST.map((t) => (
                <span key={t.label} className="flex items-center gap-2 bg-gold/10 text-gold border border-gold/30 px-4 py-2 rounded-full text-xs font-mono tracking-widest uppercase">
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
